import axios from "./axios";
import { clearReferral, getReferralCodeForSpace } from "../utils/referral";
import { notifyMintSpaceActivity } from "../utils/mintSpaceRecent";

function afterMintSpaceMutation(spaceId, result) {
  if (spaceId && result?.success !== false) {
    notifyMintSpaceActivity(spaceId);
  }
  return result;
}

export const createMintSpace = async (mintSpaceData) => {
  try {
    const response = await axios.post("/mint-space", mintSpaceData);
    const result = response.data;
    if (result?.data?._id) afterMintSpaceMutation(result.data._id, result);
    return result;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getAllMyMintSpaces = async (userId = null) => {
  try {
    const url = userId ? `/mint-space?userId=${userId}` : "/mint-space";
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Public discover — search mint spaces with campaign / submission stats */
export const getDiscoverMintSpaces = async (search = "", category = "", { joined = false, mine = false } = {}) => {
  try {
    const params = new URLSearchParams();
    if (search && search.trim()) params.append("search", search.trim());
    if (category && category.trim() && category !== "all") {
      params.append("category", category.trim());
    }
    if (joined) params.append("joined", "1");
    if (mine) params.append("mine", "1");
    const qs = params.toString();
    const response = await axios.get(`/mint-space/discover${qs ? `?${qs}` : ""}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMintSpaceById = async (id) => {
  try {
    const response = await axios.get(`/mint-space/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Public buyer-facing product details. productId is optional (falls back to the first product).
export const getMintSpaceProduct = async (spaceId, productId) => {
  try {
    const url = productId
      ? `/mint-space/${spaceId}/product/${productId}`
      : `/mint-space/${spaceId}/product`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Public product reviews + rating summary.
export const getProductReviews = async (spaceId, productId, limit) => {
  try {
    const q = limit ? `?limit=${limit}` : "";
    const response = await axios.get(`/mint-space/${spaceId}/product/${productId}/reviews${q}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Create / update the current user's review for a product (auth required).
export const createProductReview = async (spaceId, productId, payload) => {
  try {
    const response = await axios.post(
      `/mint-space/${spaceId}/product/${productId}/reviews`,
      payload
    );
    return afterMintSpaceMutation(spaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateMintSpace = async (id, mintSpaceData) => {
  try {
    const response = await axios.put(`/mint-space/${id}`, mintSpaceData);
    return afterMintSpaceMutation(id, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const installApp = async (mintSpaceId, appId, visibility) => {
  try {
    const response = await axios.post(`/mint-space/${mintSpaceId}/install-app`, { appId, visibility });
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** The coming-soon apps you asked to hear about. Add and remove answer with the new list. */
export const getAppLaunchAlerts = async () => {
  const response = await axios.get("/mint-space/app-alerts");
  return response.data?.data || [];
};

export const addAppLaunchAlert = async (appId) => {
  const response = await axios.put(`/mint-space/app-alerts/${appId}`);
  return response.data?.data || [];
};

export const removeAppLaunchAlert = async (appId) => {
  const response = await axios.delete(`/mint-space/app-alerts/${appId}`);
  return response.data?.data || [];
};

export const uninstallApp = async (mintSpaceId, appId, instanceId) => {
  try {
    const body = instanceId ? { instanceId } : { appId };
    const response = await axios.post(`/mint-space/${mintSpaceId}/uninstall-app`, body);
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Update installed app display settings (rename + public/private). */
export const updateAppSettings = async (mintSpaceId, settings) => {
  try {
    const response = await axios.put(`/mint-space/${mintSpaceId}/app-settings`, settings);
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Create a new product or update an existing one (pass `productId` to update). */
export const updateAppPricing = async (mintSpaceId, pricing) => {
  try {
    const response = await axios.put(`/mint-space/${mintSpaceId}/app-pricing`, pricing);
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Delete a single product by its id. */
export const deleteAppPricing = async (mintSpaceId, productId) => {
  try {
    const response = await axios.delete(`/mint-space/${mintSpaceId}/app-pricing/${productId}`);
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** What stands in the way of deleting this Mint Space, and what deleting it would touch. Owner only. */
export const getMintSpaceDeletionCheck = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/deletion-check`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Delete a Mint Space for good. `confirmation` is its name, typed out by the owner. */
export const deleteMintSpace = async (mintSpaceId, confirmation) => {
  try {
    const response = await axios.delete(`/mint-space/${mintSpaceId}`, { data: { confirmation } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Join (or request to join) a mint space. Any affiliate code the visitor arrived with is
 * attached automatically so the referrer gets credited — see utils/referral.
 */
export const joinMintSpace = async (mintSpaceId, answers = []) => {
  try {
    const referralCode = getReferralCodeForSpace(mintSpaceId);
    const response = await axios.post(`/mint-space/${mintSpaceId}/join`, {
      answers,
      referralCode,
    });
    // The code has been spent — keeping it would misattribute the next space they join.
    if (referralCode && response.data?.success) clearReferral();
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getJoinRequests = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/requests`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const handleJoinRequest = async (mintSpaceId, requestId, action) => {
  try {
    const response = await axios.put(`/mint-space/${mintSpaceId}/requests/${requestId}`, { action });
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const leaveMintSpace = async (mintSpaceId) => {
  try {
    const response = await axios.post(`/mint-space/${mintSpaceId}/leave`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMintSpaceMembers = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/members`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const toggleModerator = async (mintSpaceId, userId, action) => {
  try {
    const response = await axios.put(`/mint-space/${mintSpaceId}/moderators/${userId}`, { action });
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const assignTeamRole = async (mintSpaceId, userId, role) => {
  try {
    const response = await axios.put(`/mint-space/${mintSpaceId}/team/${userId}`, { role });
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const startSupportThread = async (mintSpaceId) => {
  try {
    const response = await axios.post(`/mint-space/${mintSpaceId}/support/start`);
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getSupportThreads = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/support/threads`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMySupportThread = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/support/me`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const endSupportConversation = async (mintSpaceId, conversationId) => {
  try {
    const response = await axios.post(`/mint-space/${mintSpaceId}/support/end`, { conversationId });
    return afterMintSpaceMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMintSpaceRating = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/rating`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Recent Team activity for a mint space (owner/moderator only). Backend caps `limit` at 500.
 */
export const getMintSpaceActivity = async (mintSpaceId, limit = 100) => {
  try {
    const response = await axios.get(
      `/mint-space/${mintSpaceId}/activity?limit=${limit}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Financial report for a Mint Space. `type` is one of overview | revenue | spend |
 * subscriptions | refunds | dashboard. `from` / `to` are ISO dates; both optional (defaults to the
 * last 30 days on the server).
 */
/**
 * Every sale of a Mint Space, newest first, a page at a time (owner only). Filters:
 * `product` (id), `status` (paid | refunded | trial), `q` (buyer name), `buyer` (user id).
 * `purchase` returns the page that holds that sale.
 */
export const getMintSpaceSales = async (mintSpaceId, params = {}) => {
  try {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") query.append(key, value);
    }
    const response = await axios.get(`/mint-space/${mintSpaceId}/sales?${query.toString()}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMintSpaceReport = async (mintSpaceId, { type = "overview", from, to } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("type", type);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    const response = await axios.get(
      `/mint-space/${mintSpaceId}/reports?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMintSpaceBalance = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/balance`);
    const data = response.data?.data || response.data;
    return {
      amount: Number(data?.amount) || 0,
      holdBalance: Number(data?.pendingAmount) || 0,
    };
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const transferMintSpaceToPersonal = async (mintSpaceId, amount) => {
  try {
    const response = await axios.post(
      `/mint-space/${mintSpaceId}/balance/transfer-to-personal`,
      { amount }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const transferPersonalToMintSpace = async (mintSpaceId, amount) => {
  try {
    const response = await axios.post(
      `/mint-space/${mintSpaceId}/balance/transfer-from-personal`,
      { amount }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Buyers' refund requests for this business, undecided first — owner only. */
export const getRefundRequests = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/refund-requests`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Accept a refund request: the purchase is refunded in full through Keepz. */
export const acceptRefundRequest = async (mintSpaceId, requestId) => {
  try {
    const response = await axios.post(
      `/mint-space/${mintSpaceId}/refund-requests/${requestId}/accept`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Reject a refund request, optionally saying why — the buyer is told. */
export const rejectRefundRequest = async (mintSpaceId, requestId, reason = "") => {
  try {
    const response = await axios.post(
      `/mint-space/${mintSpaceId}/refund-requests/${requestId}/reject`,
      { reason }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** This business's promo codes, with its products and affiliates — owner only. */
export const getPromoCodes = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/promo-codes`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Make a promo code. It works on no products until they're chosen. */
export const createPromoCode = async (mintSpaceId, fields) => {
  try {
    const response = await axios.post(`/mint-space/${mintSpaceId}/promo-codes`, fields);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Choose which products a promo code works on: all of them, or the ones listed. */
export const setPromoCodeProducts = async (mintSpaceId, promoId, { appliesToAll, productIds }) => {
  try {
    const response = await axios.put(
      `/mint-space/${mintSpaceId}/promo-codes/${promoId}/products`,
      { appliesToAll, productIds }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Stop a promo code working, now. */
export const expirePromoCode = async (mintSpaceId, promoId) => {
  try {
    const response = await axios.post(`/mint-space/${mintSpaceId}/promo-codes/${promoId}/expire`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Where this business's verification (KYB) stands, with what was submitted — or, if
 * nothing was yet, what the form can start with. Owner only.
 */
export const getMintSpaceKyb = async (mintSpaceId) => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/kyb`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Submit or resubmit this business's verification. It waits for an admin's review. */
export const submitMintSpaceKyb = async (mintSpaceId, form) => {
  try {
    const response = await axios.put(`/mint-space/${mintSpaceId}/kyb`, form);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
